// --- Core ---
import {ReactElement} from "react";
import {FormattedMessage} from "react-intl";
import {any, intersection, isEmpty, map, pick, pluck, some, uniq} from "underscore";
import isEmail from 'validator/lib/isEmail';
import isURL from 'validator/lib/isURL';
import {isBefore, isDate, isFuture, isPast, isValid} from 'date-fns';
// --- SaniQ ---
import {FormatDateTime} from "shared/DateTime";

const RequiredError = () => (
  <FormattedMessage id="validation.required" defaultMessage="Input is required"/>
);

const MinCharactersError = ({min}) => (
  <FormattedMessage id="validation.string.minCharacters" defaultMessage="Minimum {count} characters" values={{count: min}}/>
);

const MaxCharactersError = ({max}) => (
  <FormattedMessage id="validation.string.maxCharacters" defaultMessage="Maximum {count} characters" values={{count: max}}/>
);

const EmailError = () => (
  <FormattedMessage id="validation.string.email" defaultMessage="Input is not a valid email"/>
);

const composeValidators = (...validators) => (value, allValues, meta) =>
  validators.reduce((error, validator) => error || (validator ? validator(value, allValues, meta) : undefined), undefined);

const required = function (value) {
  if (value === undefined || value === null || (typeof value === "string" && isEmpty(value))) {
    return <RequiredError/>;
  }
};

const conditionalRequired = (condition) => condition ? required : undefined;

const isEqual = function (targetValue) {
  return value => {
    if (value !== targetValue) {
      return (
        <FormattedMessage
          id="validation.is_equal" defaultMessage="This is an incorrect entry. <b>{value}</b> expected."
          values={{value: targetValue}}
        />
      );
    }
  };
};

// String validation
const minCharacters = function (min) {
  return value => {
    if (value && value.length < min) {
      return <MinCharactersError min={min}/>;
    }
  };
};

const maxCharacters = function (max) {
  return value => {
    if (value && value.length > max) {
      return <MaxCharactersError max={max}/>;
    }
  };
};

const email = function (value) {
  if (value && !isEmail(value)) {
    return <FormattedMessage id="validation.string.email" defaultMessage={"Input is not a valid email"}/>;
  }
};

const onlyCharacters = function (value) {
  if (value && !/^[A-Za-z_][A-Za-z0-9_]*$/.test(value)) {
    return <FormattedMessage id="validation.string.only_characters" defaultMessage={"Input has special characters"}/>;
  }
};

const lowercase = function (value) {
  if (value && value !== value.toLowerCase()) {
    return <FormattedMessage id="validation.string.lowercase" defaultMessage={"Input is not lowercased"}/>;
  }
};

const noLeadingDigit = function (value) {
  if (value && /^\d/.test(value)) {
    return <FormattedMessage id="validation.string.no_leading_digit" defaultMessage={"Input has leading digit."}/>;
  }
};

const url = function (value) {
  if (value && !isURL(value)) {
    return <FormattedMessage id="validation.string.url" defaultMessage={"Input is not a valid URL"}/>;
  }
};

const regex = function (pattern, error) {
  return (value) => {
    if (value && !RegExp(pattern).test(value)) {
      return error;
    }
  };
};

const color = regex(/^#[A-F\d]{6}$/i, <FormattedMessage id="validation.string.color" defaultMessage="must have 6-digit hex format with leading #"/>);

// Autocomplete option validation
const optionRequired = (value) => {
  if (!value || !value.text) {
    return <RequiredError/>;
  }
};

const minOptionCharacters = (min) => (
  value => {
    if (value && value.text && value.text.length < min) {
      return <MinCharactersError min={min}/>;
    }
  }
);

const maxOptionCharacters = (max) => (
  value => {
    if (value && value.text && value.text.length > max) {
      return <MaxCharactersError max={max}/>;
    }
  }
);

const valueIsOptionKey = (options) => (
  value => {
    if (value && value.text && some(options, (option) => option.key === value.text) && !some(options, (option) => option.text === value.text)) {
      return <FormattedMessage id="validation.string.valueIsOptionKey" defaultMessage={"This input is not allowed"}/>;
    }
  }
);

// MultiSelect validation
const multiRequired = (value: unknown[]) => {
  if (!value || !value.length) {
    return <RequiredError/>;
  }
};

const multiRequiredWithMessage = (message: ReactElement) => (value: unknown[]) => multiRequired(value) && message;

/**
 * Validates that at least one element is present in each of the arrays
 * Returns an error message if not.
 *
 * @param property The values property that will be checked against the target elements
 * @param {array} targetElements array containing the elements that are to be validated.
 * @param count The exact count of required elements in the intersection
 * @returns {function} an validator that returns a error if not exactly the count or at least one element is in the values
 */
const hasIntersection = (property, targetElements, count) => (values) => {
  const options = property ? pluck(values, property) : values;
  const isIncluded = intersection(options, targetElements);

  //TODO(Keune): Translation of target elements in error message
  if (count) {
    if (isIncluded.length !== count) {
      return (
        <FormattedMessage
          id="validation.intersection.exactly"
          defaultMessage="Exactly {count} elements of {elements} must be included"
          values={{count, elements: targetElements.join(', ')}}
        />
      );
    }
  } else if (isIncluded.length < 1) {
    return (
      <FormattedMessage
        id="validation.intersection.at_least"
        defaultMessage="At least 1 element of {elements} must be included"
        values={{elements: targetElements.join(', ')}}
      />
    );
  }
};

/**
 * Validates that all field-array items have a unique attribute value.
 * Returns an error message if not.
 *
 * @param {string} attribute The name of the attribute that is compared
 * @returns {function} an validator that returns a string error if the specified attribute is not unique
 */
const areUnique= function<T,>(attribute: keyof T, label: ReactElement = undefined) {
  // eslint-disable-next-line react/display-name
  return (values: T[]) => {
    // eslint-disable-next-line react/destructuring-assignment
    if (values && uniq(pluck(values, attribute as string)).length < values.length) {
      if (label) {
        return <FormattedMessage id="validation.uniqueWithLabel" defaultMessage="{label} is not unique" values={{label}}/>;
      } else {
        return <FormattedMessage id="validation.unique" defaultMessage="Input is not unique"/>;
      }
    }
  };
};


/**
 * Validates that all field-array items have a unique attributes tuple.
 * Returns an error message if not.
 *
 * @param {string} attributes The names of the attributes that are compared
 * @returns {function} an validator that returns a string error if the specified attributes are not unique
 */
const areUniqueList = function (attributes) {
  return values => {
    if (values) {
      const picked = map(values, value => Object.entries(pick(value, attributes)).join(","));

      if (uniq(picked).length < picked.length) {
        return <FormattedMessage id="validation.unique" defaultMessage="Input is not unique"/>;
      }
    }
  };
};

// Number validation
const isNumber = function (value) {
  if (isNaN(value) && value !== undefined && value !== null) {
    return <FormattedMessage id="validation.number" defaultMessage="Must be a number"/>;
  }
};

const maxExcludingValue = function (max) {
  return value => {
    if (value && parseFloat(value) >= max) {
      return (
        <FormattedMessage
          id="validation.number.maximumExcluding" defaultMessage="Must be less than {value}"
          values={{value: max}}
        />
      );
    }
  };
};

const minValue = function (min) {
  return value => {
    if (!isNaN(value) && parseFloat(value) < min) {
      return (
        <FormattedMessage
          id="validation.number.minimum" defaultMessage="Must be at least {value}"
          values={{value: min}}
        />
      );
    }
  };
};

const maxValue = function (max) {
  return value => {
    if (!isNaN(value) && parseFloat(value) > max) {
      return (
        <FormattedMessage
          id="validation.number.maximum" defaultMessage="Must be at most {value}"
          values={{value: max}}
        />
      );
    }
  };
};

// Date validation
const dateRequired = function (value) {
  if (!value || !isDate(value) || !isValid(value)) {
    return <RequiredError/>;
  }
};

const dateFuture = function (value) {
  if (value && isDate(value) && !isFuture(value)) {
    return <FormattedMessage id="validation.date.future" defaultMessage="The date must be in the future"/>;
  }
};

const dateNotFuture = function (value) {
  if (value && isDate(value) && !isPast(value)) {
    return <FormattedMessage id="validation.date.not_future" defaultMessage="The date must not be in the future"/>;
  }
};

const dateValid = function (value) {
  if (value && isDate(value) && !isValid(value)) {
    return <FormattedMessage id="validation.date.invalid" defaultMessage="The date is not valid"/>;
  }
};

const dateIsGreaterThan = function (compareDate: Date) {
  return (value: unknown) => {
    if (compareDate && value && isDate(value) && !isBefore(compareDate, value)) {
      return (
        <FormattedMessage
          id="validation.date.greater_than" defaultMessage="The date must be after {value}"
          values={{value: <FormatDateTime value={compareDate}/>}}
        />
      );
    }
  };
};

// File validation
const fileRequired = function (value) {
  if (!value || value.length !== 1) {
    return <RequiredError/>;
  }
};

// CheckBoxGroup validation
const checkBoxGroupAny = function (value) {
  if (!value || !any(pluck(value, 'checked'))) {
    return <RequiredError/>;
  }
};

// Medication dosage validation
const maxDigits = function (value) {
  if (value && parseFloat(value).toString().length > 4) {
    return (
      <FormattedMessage
        id="validation.dosage.too_many_characters"
        defaultMessage="The dosage has too many characters. Maximum 4 in total are allowed"
      />
    );
  }
};

const dosage = composeValidators(isNumber, minValue(0), maxValue(9999), maxDigits);

export {
  RequiredError,
  EmailError,
  areUnique,
  areUniqueList,
  color,
  composeValidators,
  conditionalRequired,
  required,
  isEqual,
  minCharacters,
  maxCharacters,
  email,
  onlyCharacters,
  noLeadingDigit,
  url,
  regex,
  optionRequired,
  minOptionCharacters,
  maxOptionCharacters,
  valueIsOptionKey,
  multiRequired,
  multiRequiredWithMessage,
  isNumber,
  maxExcludingValue,
  minValue,
  maxValue,
  dateFuture,
  dateRequired,
  dateNotFuture,
  dateValid,
  dateIsGreaterThan,
  fileRequired,
  checkBoxGroupAny,
  dosage,
  lowercase,
  hasIntersection
};
